import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type AutoSuggestOption } from '@pkgs/components/components/AutoComplete/AutoComplete';
import { getExposePath } from '@ev-tech/search-modules-api';

import { getAutosuggestionQueryKey } from './queryKeys';
import { useAppContext } from '../useAppContext';
import { useSessionToken } from '../stores/useSessionToken';
import { useSearchStore } from '../stores/search/useSearchStore';
import { SearchMode } from '../stores/search/slices/searchModeSlice';

export const useAutoCompleteQuery = (text: string, initialLocation: string) => {
  const {
    api,
    countryCode,
    options: { language },
  } = useAppContext();

  const searchMode = useSearchStore((state) => state.searchMode);
  const modalSearchMode = useSearchStore((state) => state.modalSearchMode);

  const sessionToken = useSessionToken((state) => state.sessionToken);

  const { data = [], isFetching } = useQuery<AutoSuggestOption[]>({
    queryKey: getAutosuggestionQueryKey(text),
    queryFn: async () => {
      const result = await api.getSuggestions(text, {
        language,
        limit: 5,
        sessionToken,
      });

      if (result.status === 'error') throw result.error;

      if (result.data.type === 'locations') {
        return result.data.data.map(({ types, description, placeId }) => ({
          types: types ?? [],
          title: description ?? '',
          isProperty: false,
          id: placeId,
        }));
      }

      return result.data.data.map((property) => ({
        id: property.id,
        title: property.displayId,
        description: property.profile?.title ?? '',
        link: getExposePath(property, language, countryCode),
        isProperty: true,
      }));
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled: (searchMode === SearchMode.CLASSIC || modalSearchMode === SearchMode.CLASSIC) && text.length >= 3 && text !== initialLocation,
  });

  return { data, isFetching };
};
